/**
 * The five questions after a visit (the volunteer sign-out form, decided 29 Sep): what time did you get back;
 * any additional stops, and where (plain text); was everything okay; was there an incident (Yes opens the
 * incident report); anything else. One page, on the volunteer's phone or entered by staff at the desk.
 */
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CompletionSchema, minutesLabel, type CompletionInput } from '@shared/volunteers';
import { TimeSelect, valueOfMinutes } from './TimeSelect';
import { useAnnounce } from '@/components/Announcer';
import { ChoiceField, TextArea, TextField, toErrors, type Errors } from '@/features/people/fields';
import { Button } from '@/components/ui/button';

export function CompletionForm({ onSubmit, busy, big, submitLabel, scheduledMinutes }: { onSubmit: (c: CompletionInput) => void; busy?: boolean; big?: boolean; submitLabel: string; /** when the visit was booked to start, minutes after midnight */ scheduledMinutes?: number }) {
  const { t } = useTranslation();
  const announce = useAnnounce();
  const uid = useId();
  const [c, setC] = useState<Partial<CompletionInput>>({ startAt: scheduledMinutes === undefined ? '' : valueOfMinutes(scheduledMinutes), backAt: '', stops: '', anythingElse: '' });
  const [errs, setErrs] = useState<Errors>({});
  const set = (k: keyof CompletionInput, v: string) => setC((x) => ({ ...x, [k]: v }));
  const go = () => {
    const p = CompletionSchema.safeParse(c);
    if (!p.success) {
      setErrs(toErrors(p.error));
      announce(t('vol.form.fixErrors'), 'assertive');
      return;
    }
    setErrs({});
    onSubmit(p.data);
  };
  const yn = [
    { value: 'yes' as const, label: t('vol.yes') },
    { value: 'no' as const, label: t('vol.no') },
  ];
  return (
    <div className="completion-form" data-testid="completion-form">
      {/* 1104 (Eli): question 1 shows when it was scheduled, then "What time did you start?" (set to the scheduled time —
          change it if you were late) and "What time did you get back?": each three drop-downs, hour · minutes · AM or PM.
          Nothing to type: an iPhone's number pad has no colon, so the old box could not be filled. */}
      <div className="field completion-times" role="group" aria-labelledby={`${uid}-times-h`} data-testid="completion-times">
        <p className="label" id={`${uid}-times-h`} style={{ fontWeight: 600 }}>
          {t('vol.done.q1')}
        </p>
        {scheduledMinutes !== undefined ? (
          <p className="help" data-testid="completion-scheduled">
            {t('vol.done.scheduled', { time: minutesLabel(scheduledMinutes) })}
          </p>
        ) : null}
        <TimeSelect id={`${uid}-start`} legend={t('vol.done.startQ')} value={c.startAt ?? ''} onChange={(v) => set('startAt', v)} invalid={!!errs.startAt} describedBy={errs.startAt ? `${uid}-start-err` : undefined} testId="completion-start" />
        {errs.startAt ? (
          <p className="field-err" id={`${uid}-start-err`}>
            {t('people.err.time')}
          </p>
        ) : null}
        <TimeSelect id={`${uid}-back`} legend={t('vol.done.backQ')} required value={c.backAt ?? ''} onChange={(v) => set('backAt', v)} invalid={!!errs.backAt} describedBy={errs.backAt ? `${uid}-back-err` : undefined} testId="completion-back" />
        {errs.backAt ? (
          <p className="field-err" id={`${uid}-back-err`} data-testid="completion-back-err">
            {t('vol.done.backNeeded')}
          </p>
        ) : null}
      </div>
      <TextField id={`${uid}-stops`} label={t('vol.done.q2')} value={c.stops ?? ''} onChange={(v) => set('stops', v)} placeholder="CVS, Chase Bank" help={t('vol.done.q2Help')} />
      <ChoiceField id={`${uid}-ok`} label={t('vol.done.q3')} required value={(c.allOkay ?? '') as 'yes' | 'no' | ''} onChange={(v) => set('allOkay', v)} options={yn} error={errs.allOkay ? 'required' : undefined} />
      <ChoiceField id={`${uid}-inc`} label={t('vol.done.q4')} required value={(c.incident ?? '') as 'yes' | 'no' | ''} onChange={(v) => set('incident', v)} options={yn} error={errs.incident ? 'required' : undefined} help={t('vol.done.q4Help')} />
      <TextArea id={`${uid}-else`} label={t('vol.done.q5')} value={c.anythingElse ?? ''} onChange={(v) => set('anythingElse', v)} />
      <Button plain type="button" className={big ? 'big-btn' : 'btn'} onClick={go} disabled={busy} data-testid="completion-send">
        {submitLabel}
      </Button>
    </div>
  );
}
